import { useState } from "react"

import InputError from "@/components/input-error"
import { CurrencyInput } from "@/components/ui/currency-input"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/text-area"

interface AgentArchetypeFormProps {
  data: {
    agent_archetype: {
      name: string
      description: string
      long_description: string
      benefit: string
      duration: string
      wholesale_price: number
      illustration_svg: string
      icon: string
      outcome: string
      activation_graphic: File | null
    }
  }
  setData: (key: string, value: any) => void
  errors: Record<string, any>
  outcomes?: string[]
  // IMP-244: URL of the currently-attached activation graphic (edit only).
  activationGraphicUrl?: string | null
}

const OUTCOME_LABELS: Record<string, string> = {
  "increase-sales": "Increase Sales",
  "boost-productivity": "Boost Productivity",
  "improve-retention": "Improve Retention",
}

export default function AgentArchetypeForm({
  data,
  setData,
  errors,
  outcomes = [],
  activationGraphicUrl,
}: AgentArchetypeFormProps) {
  const svg = data.agent_archetype.illustration_svg
  const outcomeValue = data.agent_archetype.outcome || "none"
  const [graphicPreview, setGraphicPreview] = useState<string | null>(null)

  function handleGraphicChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setData("agent_archetype", {
      ...data.agent_archetype,
      activation_graphic: file,
    })
    setGraphicPreview(URL.createObjectURL(file))
  }

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent_archetype.name}
          placeholder="Agent name"
          onChange={(e) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              name: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.name ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          className="mt-1 block w-full"
          defaultValue={data.agent_archetype.description}
          placeholder="What does this agent do?"
          onChange={(e) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              description: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          Short summary. Shown on catalog cards and explore listings.
        </p>
        <InputError message={(errors as any)?.description ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="long_description">Long Description</Label>
        <Textarea
          id="long_description"
          className="mt-1 block w-full"
          rows={6}
          defaultValue={data.agent_archetype.long_description}
          placeholder="Detailed description shown on the activation page…"
          onChange={(e) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              long_description: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          Supports markdown. Use <code className="font-mono">- item</code> for
          bullet points. Shown on the agent activation page.
        </p>
        <InputError message={(errors as any)?.long_description ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="benefit">Benefit</Label>
        <Input
          id="benefit"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent_archetype.benefit}
          placeholder="e.g. 10x conversion rate"
          onChange={(e) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              benefit: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          Shown as a badge on the agent card in the Explore page. Color matches
          the outcome.
        </p>
        <InputError message={(errors as any)?.benefit ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="duration">Duration</Label>
        <Input
          id="duration"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent_archetype.duration}
          placeholder="e.g. 5 mins"
          onChange={(e) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              duration: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          Shown as a blue badge on the agent activation page.
        </p>
        <InputError message={(errors as any)?.duration ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="wholesale_price">Wholesale price (per month)</Label>
        <CurrencyInput
          id="wholesale_price"
          value={data.agent_archetype.wholesale_price}
          onChange={(value) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              wholesale_price: value ?? 0,
            })
          }
        />
        <InputError
          message={(errors as any)?.wholesale_price_cents ?? undefined}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="icon">Icon</Label>
        <Input
          id="icon"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent_archetype.icon}
          placeholder="PhoneCall, Lightning, Receipt, ChatCircle, Robot, Star…"
          onChange={(e) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              icon: e.target.value,
            })
          }
        />
        <p className="text-muted-foreground text-xs">
          Phosphor icon name (e.g. PhoneCall). Shown on the agent catalog card.
        </p>
        <InputError message={(errors as any)?.icon ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="outcome">Outcome</Label>
        <Select
          value={outcomeValue}
          onValueChange={(value) =>
            setData("agent_archetype", {
              ...data.agent_archetype,
              outcome: value === "none" ? "" : value,
            })
          }
        >
          <SelectTrigger id="outcome" className="mt-1 w-full">
            <SelectValue placeholder="Select an outcome…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {outcomes.map((o) => (
              <SelectItem key={o} value={o}>
                {OUTCOME_LABELS[o] ?? o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          Drives the catalog filter pills in the user-facing Explore tab.
        </p>
        <InputError message={(errors as any)?.outcome ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="illustration_svg">Illustration</Label>
        <div className="flex gap-3">
          <Textarea
            id="illustration_svg"
            className="mt-1 block w-full font-mono text-xs"
            rows={6}
            value={svg}
            placeholder="Paste SVG markup here…"
            onChange={(e) =>
              setData("agent_archetype", {
                ...data.agent_archetype,
                illustration_svg: e.target.value,
              })
            }
          />
          {svg && (
            <div
              className="text-muted-foreground mt-1 h-24 w-40 shrink-0 overflow-hidden rounded-md border"
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          )}
        </div>
        <InputError message={(errors as any)?.illustration_svg ?? undefined} />
      </div>

      <div className="grid gap-2">
        <ImageUpload
          label="Activation graphic"
          id="activation_graphic"
          variant="logo"
          preview={graphicPreview}
          currentUrl={activationGraphicUrl ?? undefined}
          onFileChange={handleGraphicChange}
          error={(errors as any)?.activation_graphic ?? undefined}
          buttonLabel="Upload graphic"
        />
        <p className="text-muted-foreground text-xs">
          Shown on the agent activation page. PNG, JPG, GIF, or WebP.
        </p>
      </div>
    </>
  )
}
